import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, CardHeader,
  Typography, Stack, Divider, Button, Avatar
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";
import { AcUnit } from '@mui/icons-material';

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Roomfacilities() {
  const auth = getAuth();
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [roomFacilities, setRoomFacilities] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setUserData(user));
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      if (!userData) return;
      const roleRef = doc(db, "authroles", userData.uid);
      const snap = await getDoc(roleRef);
      if (snap.exists()) setPropertyId(snap.data().propertyId || null);
    };
    fetchRole();
  }, [userData]);

  useEffect(() => {
    const fetchFacilities = async () => {
      const snap = await getDocs(collection(db, "facilities"));
      const list = snap.docs.map(d => d.data().facility);
      setFacilities(list);
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      if (!userData || !propertyId) return;
      const snap = await getDocs(collection(db, "rooms"));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.userId === userData.uid && r.propertyId === propertyId);
      setRooms(list);
      setLoading(false);

      list.forEach(async room => {
        const rfDoc = doc(db, "roomfacilities", room.id);
        const rfSnap = await getDoc(rfDoc);
        setRoomFacilities(prev => ({
          ...prev,
          [room.id]: rfSnap.exists() ? rfSnap.data().facilities : []
        }));
      });
    };
    fetchRooms();
  }, [userData, propertyId]);

  const toggleFacility = (roomId, facility) => {
    const current = roomFacilities[roomId] || [];
    const updated = current.includes(facility)
      ? current.filter(f => f !== facility)
      : [...current, facility];
    setRoomFacilities(prev => ({ ...prev, [roomId]: updated }));
  };

  const saveFacilities = async (roomId) => {
    const facilitiesToSave = roomFacilities[roomId] || [];
    await setDoc(doc(db, "roomfacilities", roomId), {
      facilities: facilitiesToSave,
      roomId,
      updatedAt: new Date()
    });
    Swal.fire({
      icon: "success",
      title: "Saved!",
      text: "Room facilities updated successfully.",
      timer: 1500,
      showConfirmButton: false
    });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
        <Typography variant="h5" fontWeight={700} mb={3} sx={{ color: colors.vanilla }}>
          🛎️ Manage Room Facilities
        </Typography>

        {loading && <Typography sx={{ color: colors.vanilla }}>Loading...</Typography>}
        {!loading && rooms.length === 0 && <Typography sx={{ color: colors.vanilla }}>No rooms found for this property</Typography>}

        <div className="container-fluid p-0">
          <div className="row">
            {rooms.map(room => (
              <div className="col-sm-12 mb-4" key={room.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${colors.goldenrod}`,
                    bgcolor: colors.calPolyGreen,
                    color: colors.vanilla,
                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 12px 30px rgba(0,0,0,0.5)" }
                  }}
                >
                  <CardHeader
                    title={<Typography fontWeight={700} sx={{ color: colors.gold }}>{room.roomTypeName}</Typography>}
                    subheader={<Typography variant="caption" sx={{ color: colors.vanilla, opacity: 0.85 }}>Beds: {room.bed?.count} | {room.bed?.type}</Typography>}
                    sx={{ pb: 0 }}
                  />
                  <Divider sx={{ my: 1, borderColor: colors.goldenrod, opacity: 0.4 }} />

                  <CardContent sx={{ p: 3 }}>
                    <Typography fontWeight={600} mb={1} sx={{ color: colors.vanilla }}>Facilities</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                      {facilities.map(facility => {
                        const selected = roomFacilities[room.id]?.includes(facility) || false;
                        return (
                          <Box
                            key={facility}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              p: 1,
                              minWidth: 120,
                              borderRadius: 2,
                              cursor: "pointer",
                              border: selected ? `2px solid ${colors.gold}` : `1px solid ${colors.goldenrod}`,
                              background: selected ? colors.darkGreen : colors.calPolyGreen,
                              color: colors.vanilla,
                              "&:hover": { transform: "scale(1.05)", transition: "0.2s" }
                            }}
                            onClick={() => toggleFacility(room.id, facility)}
                          >
                            <Avatar sx={{ bgcolor: selected ? colors.gold : colors.darkGreen, color: selected ? colors.darkGreen : colors.vanilla, width: 28, height: 28, mr: 1, border: `1px solid ${colors.goldenrod}` }}>
                              <AcUnit fontSize="small" />
                            </Avatar>
                            <Typography fontWeight={500} sx={{ fontSize: '0.85rem' }}>{facility}</Typography>
                          </Box>
                        );
                      })}
                    </Stack>

                    <Button
                      variant="contained"
                      onClick={() => saveFacilities(room.id)}
                      sx={{ 
                        bgcolor: colors.goldenrod, 
                        color: colors.darkGreen, 
                        fontWeight: 700, 
                        textTransform: "none", 
                        borderRadius: "8px",
                        '&:hover': { bgcolor: colors.gold } 
                      }}
                    >
                      Save Facilities
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Box>
  );
}

export default Roomfacilities;